/**
 * Storage Manager - Handles IndexedDB operations for saving/loading mixes & track cues
 */

export class StorageManager {
  constructor() {
    this.dbName = 'DJMashupDB'
    this.mixesStore = 'mixes'
    this.tracksStore = 'tracks'  // Track cue database
    this.searchStore = 'search_results'  // Search metadata cache
    this.db = null
    this.initializeDB()
  }

  /**
   * Initialize IndexedDB with mixes, tracks, and search results stores
   */
  async initializeDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 3)  // Increment version for schema upgrade

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result

        // Mixes store (existing)
        if (!db.objectStoreNames.contains(this.mixesStore)) {
          db.createObjectStore(this.mixesStore, { keyPath: 'id' })
        }

        // Tracks store — stores cue markers per track
        if (!db.objectStoreNames.contains(this.tracksStore)) {
          db.createObjectStore(this.tracksStore, { keyPath: 'fileBasedId' })
        }

        // Search results store — caches API results
        if (!db.objectStoreNames.contains(this.searchStore)) {
          db.createObjectStore(this.searchStore, { keyPath: 'sourceId' })
        }
      }
    })
  }

  /**
   * Save mix project
   */
  async saveMix(mix) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.mixesStore], 'readwrite')
      const store = transaction.objectStore(this.mixesStore)

      const mixData = {
        id: mix.id || new Date().getTime(),
        name: mix.name,
        createdAt: new Date().toISOString(),
        tracks: mix.tracks,
        mixSettings: mix.mixSettings,
      }

      const request = store.put(mixData)
      request.onsuccess = () => resolve(mixData.id)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Load mix project
   */
  async loadMix(id) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.mixesStore], 'readonly')
      const store = transaction.objectStore(this.mixesStore)
      const request = store.get(id)

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get all saved mixes
   */
  async getAllMixes() {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.mixesStore], 'readonly')
      const store = transaction.objectStore(this.mixesStore)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Delete mix project
   */
  async deleteMix(id) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.mixesStore], 'readwrite')
      const store = transaction.objectStore(this.mixesStore)
      const request = store.delete(id)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // ── Track Cues Database (NEW) ──────────────────────────────────────────────

  /**
   * Save track cue markers (main cue + 4 hot cues + metadata)
   * @param {string} fileBasedId - Unique identifier (SHA-256 of filename+size+duration)
   * @param {object} cueData - { fileName, fileSize, duration, mainCue, hotCues, dateAdded, playCount }
   */
  async saveTrackCues(fileBasedId, cueData) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.tracksStore], 'readwrite')
        const store = transaction.objectStore(this.tracksStore)

        const trackData = {
          fileBasedId,
          ...cueData,
          lastModified: new Date().toISOString(),
        }

        const request = store.put(trackData)
        request.onsuccess = () => resolve(fileBasedId)
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Load track cue markers by file-based ID
   * @param {string} fileBasedId - Unique identifier
   * @returns {object|null} - Cue data or null if not found
   */
  async loadTrackCues(fileBasedId) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.tracksStore], 'readonly')
        const store = transaction.objectStore(this.tracksStore)
        const request = store.get(fileBasedId)

        request.onsuccess = () => resolve(request.result || null)
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Delete track cue markers
   * @param {string} fileBasedId - Unique identifier
   */
  async deleteTrackCues(fileBasedId) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.tracksStore], 'readwrite')
        const store = transaction.objectStore(this.tracksStore)
        const request = store.delete(fileBasedId)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Get all tracked songs (for future UI: "Recent Tracks" or "Cued Songs")
   */
  async getAllTrackedCues() {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.tracksStore], 'readonly')
        const store = transaction.objectStore(this.tracksStore)
        const request = store.getAll()

        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }

  // ── Search Results Cache (NEW) ────────────────────────────────────────────

  /**
   * Cache search result in database
   * @param {object} result - {title, artist, album, duration, imageUrl, source, sourceId, preview}
   */
  async cacheSearchResult(result) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.searchStore], 'readwrite')
        const store = transaction.objectStore(this.searchStore)

        const cached = {
          ...result,
          timestamp: Date.now(),
          inPlaylist: false,
        }

        const request = store.put(cached)
        request.onsuccess = () => resolve(result.sourceId)
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Load cached search results by source
   * @param {string} source - 'lastfm', 'spotify', etc.
   * @returns {array} - Cached results
   */
  async getSearchResultsBySource(source) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.searchStore], 'readonly')
        const store = transaction.objectStore(this.searchStore)
        const index = store.index('source')
        const request = index.getAll(source)

        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Clear old search results (older than 24 hours)
   */
  async clearOldSearchResults() {
    if (!this.db) await this.initializeDB()

    const cutoffTime = Date.now() - 24 * 60 * 60 * 1000

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.searchStore], 'readwrite')
        const store = transaction.objectStore(this.searchStore)
        const request = store.getAll()

        request.onsuccess = () => {
          const results = request.result || []
          let deleteCount = 0

          results.forEach(r => {
            if (r.timestamp < cutoffTime && !r.inPlaylist) {
              store.delete(r.sourceId)
              deleteCount++
            }
          })

          resolve(deleteCount)
        }
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Mark search result as added to playlist
   */
  async markSearchResultInPlaylist(sourceId) {
    if (!this.db) await this.initializeDB()

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([this.searchStore], 'readwrite')
        const store = transaction.objectStore(this.searchStore)
        const request = store.get(sourceId)

        request.onsuccess = () => {
          const result = request.result
          if (result) {
            result.inPlaylist = true
            store.put(result)
          }
          resolve()
        }
        request.onerror = () => reject(request.error)
      } catch (err) {
        reject(err)
      }
    })
  }
}
